/**
 * Der Aufnahme-Wächter der Sprechtaste (Web). 30.10.2026
 *
 * Konto 2 nahm lange per Sprechtaste auf; die Aufnahme endete still, ohne
 * Alarm. Niemand hörte auf `ended`, `mute`, `error` oder ein unerwartetes
 * `stop`, und beim Verdecken der Seite geschah für die Aufnahme nichts.
 *
 * Dieses Modul kennt weder Browser noch Rekorder: Uhr, Zeitgeber und die
 * Lage kommen von außen (PttRecorder). Darum ist es mit gefälschter Uhr und
 * Attrappe testbar.
 *
 * Regeln:
 * - Lebenszeichen alle 2 s: wachsen die Bytes, läuft der Rekorder, lebt die
 *   Spur und ist sie nicht stumm? Zwei Takte ohne Wachstum (4 s) gelten als
 *   Ausfall. Stumm muss 3 s anhalten (kurze Unterbrechungen kommen zurück).
 * - `ended`, `error` und ein unerwartetes `stop` sind sofort Ausfall.
 * - Echte Stopps (Loslassen, Verwerfen, Höchstdauer, Leitungs- und Teilwechsel)
 *   sind nie Ausfall: wer sie auslöst, ruft vorher `ende()` oder lässt die
 *   Lage `pausiert` melden.
 * - Der Zeitgeber ist nach `ende()` und nach dem Alarm weg — es gibt genau
 *   einen Alarm je Aufnahme.
 * - Ein Zeitsprung (Seite war eingefroren) ohne Bytes ist die Rückkehr:
 *   „x Min. nicht aufgenommen“.
 */

/** Takt der Lebenszeichen. */
export const LEBENSZEICHEN_MS = 2000;
/** So lange dürfen die Bytes ausbleiben (zwei Takte). */
export const STILLSTAND_MS = 4000;
/** So lange muss die Spur stumm sein, bevor es ein Ausfall ist. */
export const STUMM_GNADE_MS = 3000;
/** Ein Takt, der so viel später kommt, war eingefroren. */
export const ZEITSPRUNG_MS = 6000;
/**
 * Wenigstens so viele Bytes je Sekunde Lücke zeigen: der Rekorder lief weiter.
 * Opus (VBR) liefert bei digitaler Stille nur wenige hundert Byte je Sekunde,
 * bei Sprache das Mehrfache (Soll 64 kbit/s = 8000 B/s). 500 B/s liegt unter
 * jeder lebenden Aufnahme und über „nichts“: wer Stille aufnimmt, gilt als
 * durchgelaufen, wer nichts liefert, nicht.
 */
export const MIN_BYTES_JE_SEKUNDE = 500;
/**
 * Nachtakt: nach einem Zeitsprung wird erst nach dieser Frist bewertet. Beim
 * Zurückkommen (oder nach einer Blockade des Haupt-Threads) liefert der Rekorder
 * seine aufgestauten Teile erst im nächsten Augenblick nach.
 */
export const NACHTAKT_MS = 400;
/** Ein gewollter Wechsel (Leitung, Teil) darf so lange dauern; danach gilt er als hängend. */
export const PAUSE_MAX_MS = 8000;

export type AusfallGrund = "ended" | "error" | "stop" | "stumm" | "still" | "zustand" | "spur" | "haengt";
export type WachterEreignis = "ended" | "error" | "stop" | "mute" | "unmute";

/** Was PttRecorder in diesem Augenblick über die Aufnahme weiß. */
export interface WachterLage {
  /** Summe aller gelieferten Bytes (wächst nur). */
  bytes: number;
  /** Rekorder im Zustand `recording`. */
  laeuft: boolean;
  /** Spur `readyState === "live"`. */
  spurLebt: boolean;
  /** Spur `muted`. */
  spurStumm: boolean;
  /** Ein gewollter Wechsel (Leitung, Teil) läuft: nichts bewerten. */
  pausiert: boolean;
}

export interface AusfallInfo {
  grund: AusfallGrund;
  /** Seit wann keine Bytes mehr kamen (ms). */
  stehtSeitMs: number;
  /** Gesicherte Bytes bis hierher. */
  bytes: number;
}

export interface RueckkehrInfo {
  /** Wie lange nichts aufgenommen wurde (ms). */
  fehltMs: number;
  bytes: number;
}

export interface WachterWege {
  lage: () => WachterLage;
  jetzt: () => number;
  /** Zeitgeber setzen; gibt eine Kennung zurück. */
  setzeTakt: (fn: () => void, ms: number) => unknown;
  loescheTakt: (kennung: unknown) => void;
  beiAusfall: (a: AusfallInfo) => void;
  beiRueckkehr: (r: RueckkehrInfo) => void;
  /** Einmal später ausführen (Nachtakt); gibt eine Kennung zurück. */
  spaeter: (fn: () => void, ms: number) => unknown;
  loescheSpaeter: (kennung: unknown) => void;
  /** Messzeile: nur Zahlen und feste Marker. */
  zeile: (text: string) => void;
}

export interface AufnahmeWachter {
  /** Beginnt (oder beginnt neu, nach einem Leitungswechsel) zu wachen. */
  start(): void;
  /** Ein Ereignis von Rekorder oder Spur. */
  ereignis(was: WachterEreignis): void;
  /** Die Seite ist wieder sichtbar: sofort prüfen, ob ein Zeitsprung war. */
  sichtbar(): void;
  /** Die Aufnahme endet gewollt: Zeitgeber weg, nie ein Alarm danach. */
  ende(): void;
  readonly aktiv: boolean;
}

/** mm:ss — wie die Uhr der Karte. */
export function standzeit(ms: number): string {
  const sek = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(sek / 60)).padStart(2, "0")}:${String(sek % 60).padStart(2, "0")}`;
}

/** Der Satz der roten Karte. */
export function ausfallSatz(stehtSeitMs: number): string {
  return `Aufnahme steht seit ${standzeit(stehtSeitMs)} – bis hier gesichert`;
}

/** Der ehrliche Satz nach der Rückkehr. */
export function rueckkehrSatz(fehltMs: number): string {
  if (fehltMs >= 60_000) {
    const min = Math.round(fehltMs / 60_000);
    return `${min} Min. nicht aufgenommen – das Vorhandene wird erkannt`;
  }
  const sek = Math.max(1, Math.round(fehltMs / 1000));
  return `${sek} Sek. nicht aufgenommen – das Vorhandene wird erkannt`;
}

export function erzeugeAufnahmeWachter(w: WachterWege): AufnahmeWachter {
  let aktiv = false;
  let takt: unknown = null;
  let letzteBytes = 0;
  let letztesWachstum = 0;
  let letzterTakt = 0;
  let stummSeit: number | null = null;
  let pauseSeit: number | null = null;
  let nachtakt: unknown = null;
  /** Lücke und Bytes-Stand, wenn der Nachtakt angesetzt wurde. */
  let sprung: { luecke: number; bytesDavor: number } | null = null;

  const stoppeTakt = (): void => {
    if (takt !== null) w.loescheTakt(takt);
    takt = null;
    if (nachtakt !== null) w.loescheSpaeter(nachtakt);
    nachtakt = null;
    sprung = null;
  };
  const beende = (): void => {
    aktiv = false;
    stoppeTakt();
    stummSeit = null;
    pauseSeit = null;
  };

  const ausfall = (grund: AusfallGrund, lage: WachterLage): void => {
    if (!aktiv) return;
    const stehtSeitMs = Math.max(0, w.jetzt() - letztesWachstum);
    beende();
    w.zeile(`aufnahme-ausfall grund=${grund} stehtMs=${Math.round(stehtSeitMs)} bytes=${lage.bytes}`);
    w.beiAusfall({ grund, stehtSeitMs, bytes: lage.bytes });
  };

  /** Der Nachtakt: jetzt erst bewerten, ob in der Lücke etwas aufgenommen wurde. */
  const entscheideSprung = (): void => {
    nachtakt = null;
    const s = sprung;
    sprung = null;
    if (!aktiv || !s) return;
    const t = w.jetzt();
    const lage = w.lage();
    // Prüfer K4: ein gewollter Wechsel im Nachtakt ist keine Lücke; zählt ein neuer Rekorder von vorn
    // (Bytes kleiner als davor), ist das ein Lebenszeichen, kein Rückschritt.
    if (lage.pausiert || lage.bytes < s.bytesDavor) {
      letzteBytes = lage.bytes;
      letztesWachstum = t;
      letzterTakt = t;
      return;
    }
    if (lage.bytes - s.bytesDavor < (s.luecke / 1000) * MIN_BYTES_JE_SEKUNDE) {
      beende();
      w.zeile(`aufnahme-rueckkehr fehltMs=${Math.round(s.luecke)} bytes=${lage.bytes}`);
      w.beiRueckkehr({ fehltMs: s.luecke, bytes: lage.bytes });
      return;
    }
    // Der Rekorder lief durch (gedrosselter Takt im Hintergrund): kein Befund.
    letzteBytes = lage.bytes;
    letztesWachstum = t;
    letzterTakt = t;
  };

  const pruefe = (): void => {
    if (!aktiv) return;
    const t = w.jetzt();
    const lage = w.lage();
    // Ein Nachtakt steht aus: er entscheidet über den Zeitsprung, nichts sonst.
    if (nachtakt !== null) return;
    const luecke = t - letzterTakt;
    const gewachsen = lage.bytes - letzteBytes;
    if (lage.pausiert) {
      pauseSeit ??= t;
      // Ein Wechsel, der nicht endet, ist kein Wechsel mehr: der Druck steht.
      if (t - pauseSeit >= PAUSE_MAX_MS) return ausfall("haengt", lage);
      letzteBytes = lage.bytes;
      letzterTakt = t;
      stummSeit = null;
      return;
    }
    if (pauseSeit !== null) {
      pauseSeit = null;
      letztesWachstum = t;
    }
    if (luecke > ZEITSPRUNG_MS) {
      sprung = { luecke, bytesDavor: letzteBytes };
      nachtakt = w.spaeter(entscheideSprung, NACHTAKT_MS);
      return;
    }
    letzterTakt = t;
    if (!lage.laeuft) return ausfall("zustand", lage);
    if (!lage.spurLebt) return ausfall("spur", lage);
    if (lage.spurStumm) {
      stummSeit ??= t;
      if (t - stummSeit >= STUMM_GNADE_MS) return ausfall("stumm", lage);
    } else {
      stummSeit = null;
    }
    if (lage.bytes < letzteBytes) {
      // Ein neuer Rekorder zählt von vorn: das ist ein Lebenszeichen, kein Rückschritt.
      letzteBytes = lage.bytes;
      letztesWachstum = t;
    } else if (lage.bytes > letzteBytes) {
      letzteBytes = lage.bytes;
      letztesWachstum = t;
    } else if (t - letztesWachstum >= STILLSTAND_MS) {
      ausfall("still", lage);
    }
  };

  return {
    get aktiv() {
      return aktiv;
    },
    start() {
      stoppeTakt();
      const t = w.jetzt();
      aktiv = true;
      letzteBytes = w.lage().bytes;
      letztesWachstum = t;
      letzterTakt = t;
      stummSeit = null;
      takt = w.setzeTakt(pruefe, LEBENSZEICHEN_MS);
    },
    ereignis(was) {
      if (!aktiv) return;
      const lage = w.lage();
      if (lage.pausiert) return;
      if (was === "mute") {
        stummSeit ??= w.jetzt();
        return;
      }
      if (was === "unmute") {
        stummSeit = null;
        return;
      }
      ausfall(was, lage);
    },
    sichtbar: pruefe,
    ende: beende,
  };
}
